import Shell from '../components/Shell';
import { C2Pill, C2Section } from '../components/c2';
import type { DecorShape } from '../components/c2/Section';
import PageHero from '../components/secondary/PageHero';
import BottomCTA from '../components/secondary/BottomCTA';

const compareDecor: DecorShape[] = [
  { shape: 'circle', size: 220, color: 'sky', style: { position: 'absolute', top: 60, left: -90, opacity: 0.4 } },
  { shape: 'pill', size: 220, color: 'lemon', style: { position: 'absolute', top: 200, right: -70, opacity: 0.4, transform: 'rotate(8deg)' } },
  { shape: 'square', size: 130, color: 'mint', style: { position: 'absolute', bottom: 80, left: '8%', opacity: 0.4, transform: 'rotate(-10deg)' } },
  { shape: 'circle', size: 180, color: 'peach', style: { position: 'absolute', bottom: 40, right: -50, opacity: 0.4 } },
];

interface CellData {
  text: string;
  good?: boolean;
  strong?: boolean;
  note?: string;
}

interface CompareRow {
  label: string;
  tidy: CellData;
  golinks: CellData;
  trotto: CellData;
}

const compareRows: CompareRow[] = [
  { label: 'Pricing',
    tidy: { text: 'Free forever', good: true, strong: true },
    golinks: { text: '$2–4/user/mo + up after first 20 users', good: false },
    trotto: { text: '$3/user/mo + up after first 10 users', good: false } },
  { label: 'Unlimited users free',
    tidy: { text: '✓', good: true, strong: true },
    golinks: { text: '—', good: false },
    trotto: { text: '—', good: false } },
  { label: 'Open source',
    tidy: { text: '✓', good: true, strong: true },
    golinks: { text: '—', good: false },
    trotto: { text: '✓', good: true } },
  { label: 'Self-host option',
    tidy: { text: '✓', good: true, strong: true },
    golinks: { text: '—', good: false },
    trotto: { text: 'No', good: false } },
  { label: 'Chrome extension',
    tidy: { text: '✓', good: true },
    golinks: { text: '✓', good: true },
    trotto: { text: '✓', good: true } },
  { label: 'Alternate go/ link names',
    tidy: { text: '✓', good: true, strong: true },
    golinks: { text: 'Unclear', good: false },
    trotto: { text: '—', good: false } },
  { label: 'Click analytics',
    tidy: { text: '✓', good: true },
    golinks: { text: '✓ (paid tiers)', good: true },
    trotto: { text: 'Basic', good: true } },
  { label: 'SSO (Google, Microsoft, Zoho)',
    tidy: { text: '✓', good: true, strong: true },
    golinks: { text: 'Google only', good: false },
    trotto: { text: 'Google only', good: false } },
  { label: 'Role-based admin',
    tidy: { text: '✓', good: true },
    golinks: { text: '✓', good: true },
    trotto: { text: 'Basic', good: true } },
  { label: 'Support',
    tidy: { text: 'Email + GitHub', good: true },
    golinks: { text: 'Tiered', good: true },
    trotto: { text: 'Community', good: true } },
];

function CompareCell({ text, good, strong, note }: CellData) {
  const isDash = text === '—';
  const isCheck = text === '✓';
  const useEmphasis = strong && !isCheck && !isDash;
  const color = isDash
    ? 'var(--color-ink-faint)'
    : useEmphasis
      ? 'var(--accent-deep)'
      : good
        ? 'var(--color-ink)'
        : 'var(--color-ink-muted)';
  return (
    <div className="inline-flex flex-col items-center gap-1">
      <span
        style={{
          fontSize: useEmphasis ? 16 : 15,
          fontWeight: useEmphasis ? 700 : 500,
          color,
        }}
      >
        {text}
      </span>
      {note && <span className="text-[11px] italic text-ink-muted">{note}</span>}
    </div>
  );
}

export default function ComparePage() {
  return (
    <Shell>
      <PageHero
        title="Compare go/ links services"
        titleAccentWord="Compare"
        titleMaxWidth={960}
        sub="Three products. Same job. You can't go wrong, but only one is free & open source."
        decorShapes={[
          { shape: 'circle', size: 340, color: 'mint', style: { position: 'absolute', top: -80, left: -120, opacity: 0.5 } },
          { shape: 'circle', size: 200, color: 'peach', style: { position: 'absolute', top: 60, right: -40, opacity: 0.55 } },
          { shape: 'pill', size: 220, color: 'lilac', style: { position: 'absolute', bottom: 0, left: '25%', opacity: 0.5, transform: 'rotate(-8deg)' } },
        ]}
      />

      <C2Section pad={12} decor={compareDecor}>
        <div
          className="bg-surface border border-hairline rounded-xl overflow-hidden"
          style={{ boxShadow: 'var(--shadow-sm)' }}
        >
          <table className="w-full border-collapse font-body">
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-hairline)' }}>
                <th className="text-left w-[260px]" style={{ padding: '18px 28px 14px' }}>
                  <span className="text-xs font-semibold text-ink-muted uppercase" style={{ letterSpacing: '0.08em' }}>Feature</span>
                </th>
                <th className="relative" style={{ padding: '18px 20px 14px', background: 'var(--accent-soft)' }}>
                  <div className="flex flex-col items-center gap-1">
                    <div
                      className="font-display font-bold"
                      style={{ fontSize: 20, color: 'var(--accent-deep)', letterSpacing: '-0.01em' }}
                    >
                      Tidy Go Links
                    </div>
                    <C2Pill color="mint" size="sm">$0 forever</C2Pill>
                  </div>
                </th>
                <th style={{ padding: '18px 20px 14px' }}>
                  <div className="flex flex-col items-center gap-1">
                    <div className="font-display font-bold text-ink" style={{ fontSize: 20 }}>GoLinks.io</div>
                  </div>
                </th>
                <th style={{ padding: '18px 20px 14px' }}>
                  <div className="flex flex-col items-center gap-1">
                    <div className="font-display font-bold text-ink" style={{ fontSize: 20 }}>Trotto</div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {compareRows.map(row => (
                <tr key={row.label} style={{ borderBottom: '1px solid var(--color-hairline)' }}>
                  <td className="text-[15px] font-semibold text-ink" style={{ padding: '11px 28px' }}>{row.label}</td>
                  <td
                    className="text-center"
                    style={{ padding: '11px 20px', background: 'var(--accent-tint)' }}
                  >
                    <CompareCell {...row.tidy} />
                  </td>
                  <td className="text-center" style={{ padding: '11px 20px' }}>
                    <CompareCell {...row.golinks} />
                  </td>
                  <td className="text-center" style={{ padding: '11px 20px' }}>
                    <CompareCell {...row.trotto} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-center text-xs text-ink-muted mt-4 leading-[1.6]">
          Numbers checked Nov 2025 from public pricing pages. We'll update this as the others change. Names are trademarks of their owners.
        </p>
      </C2Section>

      <BottomCTA
        title="Still curious?"
        titleAccent="curious"
        sub="Spin up a workspace in 90 seconds. No credit card, no sales call."
      />
    </Shell>
  );
}
